import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraFormField, SpecraInput, SpecraOverlayDialog, SpecraOverlayFooter, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCreateProject } from '../../hooks/useProjects';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
import { getProjectPath } from '../../lib/utils/project-routes';
import { FileUploader } from './FileUploader';
// CONTRACT_TYPES bundles each contract type's value, label, and the codegen
// testid pulled from `@mono/ui-selectors`. Keep this list in sync with the
// `contractTypeOption*` entries in selectors.json — the drift CI check will
// fail if you add a value here without a matching testid entry.
const CONTRACT_TYPES = [
    {
        value: 'FFP',
        label: 'Firm Fixed Price (FFP)',
        testid: Testids.project.contractTypeOptionFfp
    },
    {
        value: 'T&M',
        label: 'Time & Materials (T&M)',
        testid: Testids.project.contractTypeOptionTm
    },
    {
        value: 'CPFF',
        label: 'Cost Plus Fixed Fee (CPFF)',
        testid: Testids.project.contractTypeOptionCpff
    },
    {
        value: 'CPIF',
        label: 'Cost Plus Incentive Fee (CPIF)',
        testid: Testids.project.contractTypeOptionCpif
    },
    {
        value: 'CPAF',
        label: 'Cost Plus Award Fee (CPAF)',
        testid: Testids.project.contractTypeOptionCpaf
    },
    {
        value: 'IDIQ',
        label: 'Indefinite Delivery/Indefinite Quantity (IDIQ)',
        testid: Testids.project.contractTypeOptionIdiq
    },
    {
        value: 'BPA',
        label: 'Blanket Purchase Agreement (BPA)',
        testid: Testids.project.contractTypeOptionBpa
    },
    {
        value: 'HYBRID',
        label: 'Hybrid',
        testid: Testids.project.contractTypeOptionHybrid
    },
    {
        value: 'OTHER',
        label: 'Other',
        testid: Testids.project.contractTypeOptionOther
    }
];
const OPTIONAL = /*#__PURE__*/ _jsx("span", {
    className: "font-normal text-muted-foreground",
    children: "(optional)"
});
export function CreateProjectDialog({ open, onOpenChange, initialType }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState(initialType ?? 'general');
    const [files, setFiles] = useState([]);
    const [contractNumber, setContractNumber] = useState('');
    const [contractType, setContractType] = useState('');
    const [touched, setTouched] = useState(false);
    const createProject = useCreateProject();
    const navigate = useNavigate();
    // Apply the caller's pre-selected type each time the dialog opens (e.g. a
    // template click). Only fires on open/initialType change, so it never
    // clobbers a manual type change the user makes while the dialog is open.
    useEffect(()=>{
        if (open) setType(initialType ?? 'general');
    }, [
        open,
        initialType
    ]);
    const hasPackage = type === 'proposal' || type === 'contract';
    // Validation — name is optional for proposal projects: the backend defaults
    // it to "Untitled Proposal" and extraction renames it from the documents.
    // General and contract have no such auto-rename, so a name is still required.
    const trimmedName = name.trim();
    const isNameEmpty = trimmedName.length === 0;
    const isNameRequired = type !== 'proposal';
    const showNameError = touched && isNameEmpty && isNameRequired;
    const isValid = !isNameRequired || !isNameEmpty;
    const handleSubmit = async (e)=>{
        e.preventDefault();
        setTouched(true);
        if (!isValid) {
            return;
        }
        try {
            const result = await createProject.mutateAsync({
                name: name.trim() || undefined,
                description: description.trim() || undefined,
                type,
                files: files.length > 0 ? files : undefined,
                contractNumber: type === 'contract' ? contractNumber.trim() || undefined : undefined,
                contractType: type === 'contract' ? contractType || undefined : undefined
            });
            // Show success message
            if (result.filesUploaded && result.filesUploaded > 0) {
                toast.success(`Project created with ${result.filesUploaded} ${result.filesUploaded === 1 ? 'file' : 'files'}. Processing started automatically.`);
            } else {
                toast.success('Project created successfully');
            }
            // Reset form and close dialog
            setName('');
            setDescription('');
            setType('general');
            setFiles([]);
            setContractNumber('');
            setContractType('');
            setTouched(false);
            onOpenChange(false);
            // Navigate to appropriate page based on project type
            navigate(getProjectPath({
                type,
                projectId: result.project.projectId
            }));
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to create project'));
        }
    };
    const handleCancel = ()=>{
        setName('');
        setDescription('');
        setType('general');
        setFiles([]);
        setContractNumber('');
        setContractType('');
        setTouched(false);
        onOpenChange(false);
    };
    const handleTypeChange = (value)=>{
        setType(value);
        // Clear files when switching to general type
        if (value === 'general') {
            setFiles([]);
        }
        // Clear contract fields when switching away from contract type
        if (value !== 'contract') {
            setContractNumber('');
            setContractType('');
        }
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: (next)=>{
            // Esc, overlay click, and the X all funnel through here. While a
            // create is in flight none of them may dismiss the dialog — the
            // mutation would finish against a dialog the user believes
            // cancelled (surprise toast + navigation, or a duplicate create on
            // retry). Footer buttons already disable while pending; this
            // closes the other three exits.
            if (!next && createProject.isPending) return;
            onOpenChange(next);
        },
        testId: Testids.project.createDialog,
        title: "New Project",
        description: "Drop in your document package — the name, number, parties, and dates are read straight from it.",
        size: "lg",
        scrollBody: false,
        className: "flex max-h-[90vh] flex-col",
        children: /*#__PURE__*/ _jsxs("form", {
            onSubmit: handleSubmit,
            className: "flex min-h-0 flex-1 flex-col",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "min-h-0 flex-1 space-y-4 overflow-y-auto pr-1",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    htmlFor: "project-type",
                                    className: "block text-[length:var(--fs-ui-caption)] font-medium text-foreground",
                                    children: [
                                        "Project type ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-danger",
                                            children: "*"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: type,
                                    onValueChange: handleTypeChange,
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            id: "project-type",
                                            disabled: createProject.isPending,
                                            "data-testid": Testids.project.typeSelect,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsxs(SelectContent, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectItem, {
                                                    value: "general",
                                                    "data-testid": Testids.project.typeOptionGeneral,
                                                    children: "General"
                                                }),
                                                /*#__PURE__*/ _jsx(SelectItem, {
                                                    value: "proposal",
                                                    "data-testid": Testids.project.typeOptionProposal,
                                                    children: "Proposal"
                                                }),
                                                /*#__PURE__*/ _jsx(SelectItem, {
                                                    value: "contract",
                                                    "data-testid": Testids.project.typeOptionContract,
                                                    children: "Contract"
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                    children: [
                                        "Shaping a deal?",
                                        ' ',
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: "/capture",
                                            className: "underline underline-offset-2",
                                            "data-testid": Testids.project.pursuitCta,
                                            children: "Open a Pursuit"
                                        }),
                                        ' ',
                                        "instead of a General project."
                                    ]
                                })
                            ]
                        }),
                        hasPackage && /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    className: "block text-[length:var(--fs-ui-caption)] font-medium text-foreground",
                                    children: [
                                        "Document package ",
                                        OPTIONAL
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                    children: type === 'proposal' ? 'Drop the solicitation package. We read the title, agency, and due dates from it.' : 'Drop the contract package. We read the name, number, parties, and dates from it.'
                                }),
                                /*#__PURE__*/ _jsx(FileUploader, {
                                    files: files,
                                    onChange: setFiles,
                                    disabled: createProject.isPending,
                                    inputTestid: Testids.project.fileUploadInput
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: isNameRequired ? 'Project name' : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    "Project name ",
                                    OPTIONAL
                                ]
                            }),
                            htmlFor: "project-name",
                            required: isNameRequired,
                            error: showNameError ? 'Project name is required' : undefined,
                            hint: type === 'proposal' && isNameEmpty ? 'A name will be generated from your solicitation documents.' : `${name.length}/200 characters`,
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "project-name",
                                "data-testid": Testids.project.nameInput,
                                value: name,
                                onChange: (e)=>setName(e.target.value),
                                onBlur: ()=>setTouched(true),
                                placeholder: isNameRequired ? 'e.g. NASA Cloud Services' : 'Auto-named from your documents',
                                maxLength: 200,
                                invalid: showNameError,
                                required: isNameRequired,
                                "aria-required": isNameRequired,
                                disabled: createProject.isPending
                            })
                        }),
                        type === 'contract' && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            "Contract number ",
                                            OPTIONAL
                                        ]
                                    }),
                                    htmlFor: "contract-number",
                                    hint: "Read from your documents if left blank.",
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: "contract-number",
                                        "data-testid": Testids.project.contractNumberInput,
                                        value: contractNumber,
                                        onChange: (e)=>setContractNumber(e.target.value),
                                        placeholder: "e.g. GS-00F-0000X",
                                        disabled: createProject.isPending
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsxs("label", {
                                            htmlFor: "contract-type",
                                            className: "block text-[length:var(--fs-ui-caption)] font-medium text-foreground",
                                            children: [
                                                "Contract type ",
                                                OPTIONAL
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(Select, {
                                            value: contractType,
                                            onValueChange: setContractType,
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                                    id: "contract-type",
                                                    disabled: createProject.isPending,
                                                    "data-testid": Testids.project.contractTypeSelect,
                                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                                        placeholder: "Select contract type"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx(SelectContent, {
                                                    children: CONTRACT_TYPES.map((ct)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                            value: ct.value,
                                                            "data-testid": ct.testid,
                                                            children: ct.label
                                                        }, ct.value))
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    "Description ",
                                    OPTIONAL
                                ]
                            }),
                            htmlFor: "project-description",
                            hint: `${description.length}/2000 characters`,
                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                id: "project-description",
                                "data-testid": Testids.project.descriptionInput,
                                value: description,
                                onChange: (e)=>setDescription(e.target.value),
                                placeholder: "Add context for your team",
                                rows: 3,
                                maxLength: 2000,
                                disabled: createProject.isPending
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                    className: "-mx-4 -mb-3 mt-4 sm:-mx-5 sm:-mb-4",
                    loading: createProject.isPending,
                    loadingLabel: "Creating…",
                    confirmLabel: "Create Project",
                    confirmDisabled: createProject.isPending || touched && !isValid,
                    onCancel: handleCancel,
                    cancelProps: {
                        disabled: createProject.isPending
                    },
                    confirmProps: {
                        type: 'submit',
                        'data-testid': Testids.project.submitButton
                    }
                })
            ]
        })
    });
}
